import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faSort } from "@fortawesome/free-solid-svg-icons"

interface Column {
  accessor: string
  title: React.ReactNode
  sortable?: boolean
  render?: (record: any) => React.ReactNode
  width?: string
}

interface StickyDataTableProps {
  records: any[]
  columns: Column[]
  totalRecords: number
  recordsPerPage: number
  page: number
  onPageChange: (page: number) => void
  recordsPerPageOptions: number[]
  onRecordsPerPageChange: (pageSize: number) => void
  sortStatus?: { columnAccessor: string; direction: "asc" | "desc" }
  onSortStatusChange?: (status: { columnAccessor: string; direction: "asc" | "desc" }) => void
  minHeight?: number
  paginationText?: (info: { from: number; to: number; totalRecords: number }) => string
  highlightOnHover?: boolean
  className?: string
}

const StickyDataTable: React.FC<StickyDataTableProps> = ({
  records,
  columns,
  totalRecords,
  recordsPerPage,
  page,
  onPageChange,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  sortStatus,
  onSortStatusChange,
  paginationText,
  highlightOnHover = true,
  className = "",
}) => {
  const totalPages = Math.ceil(totalRecords / recordsPerPage)
  const startRecord = (page - 1) * recordsPerPage + 1
  const endRecord = Math.min(page * recordsPerPage, totalRecords)

  const renderCellContent = (record: any, column: Column, rowIndex: number) => {
    if (column.accessor === "id") {
      return startRecord + rowIndex
    }

    if (column.render) {
      return column.render(record)
    }

    return record[column.accessor] || ""
  }

  const handleSort = (columnAccessor: string) => {
    if (!onSortStatusChange) {
      return
    }

    const currentDirection =
      sortStatus?.columnAccessor === columnAccessor ? sortStatus.direction : null
    const newDirection = currentDirection === "asc" ? "desc" : "asc"

    onSortStatusChange({
      columnAccessor,
      direction: newDirection,
    })
  }

  const renderSortIcon = (columnAccessor: string) => {
    // Eğer bu sütun sort ediliyorsa, sort direction'a göre ikon göster
    if (sortStatus?.columnAccessor === columnAccessor) {
      return sortStatus.direction === "asc" ? (
        <FontAwesomeIcon icon={faChevronUp} className="ml-1 text-blue-600" />
      ) : (
        <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-blue-600" />
      )
    }

    // Eğer bu sütun sort edilmiyorsa ve sortable ise, default sort ikonu göster
    return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-400" />
  }

  return (
    <div className={`sticky-data-table ${className}`}>
      {/* Table Container with Sticky Headers */}
      <div className="table-container">
        <table className="w-full border-collapse">
          {/* Sticky Header */}
          <thead className="sticky-header">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  className={`px-4 py-3 text-left text-sm font-medium border-b border-gray-200 ${
                    column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                  } ${
                    sortStatus?.columnAccessor === column.accessor
                      ? "sort-active"
                      : "text-gray-700 bg-gray-50"
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.accessor)}
                >
                  <div className="flex items-center">
                    <span>{column.title}</span>
                    {column.sortable && renderSortIcon(column.accessor)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {/* Scrollable Body */}
          <tbody>
            {records.map((record, rowIndex) => (
              <tr
                key={rowIndex}
                className={`border-b border-gray-100 ${highlightOnHover ? "hover:bg-gray-50" : ""}`}
              >
                {columns.map((column) => (
                  <td key={column.accessor} className="px-4 py-3 text-sm text-gray-900">
                    {renderCellContent(record, column, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-container flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Show</span>
          <select
            value={recordsPerPage}
            onChange={(e) => onRecordsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {recordsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-700">entries</span>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">
            {paginationText
              ? paginationText({ from: startRecord, to: endRecord, totalRecords })
              : `Showing ${startRecord} to ${endRecord} of ${totalRecords} entries`}
          </span>
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>

          {/* Page Numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum
            if (totalPages <= 5) {
              pageNum = i + 1
            } else if (page <= 3) {
              pageNum = i + 1
            } else if (page >= totalPages - 2) {
              pageNum = totalPages - 4 + i
            } else {
              pageNum = page - 2 + i
            }

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`px-3 py-1 text-sm border rounded ${
                  page === pageNum
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            )
          })}

          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default StickyDataTable
